import { useState } from 'react';
import { createCharacter, setCharacterPin, type Character, ApiError } from '../../lib/api';
import { IconAvatar, type IconValue } from './IconAvatar';
import { IconPicker } from './IconPicker';
import { PinPad } from './PinPad';
import type { CosmeticInventory } from '../../lib/api';

interface Props {
  onCancel: () => void;
  onCreated: (c: Character) => void;
}

const REASONS: Record<string, string> = {
  'name-too-short': 'Name is too short.',
  'name-too-long': 'Name is too long (20 max).',
  'name-bad-chars': 'Letters, numbers, spaces only.',
  'name-blocked': 'Try a different name.',
  'bad-icon': 'Pick a face and a color.',
  'collision': 'That id collided. Try again.',
  'locked-cosmetic': 'That part unlocks after playing.',
};

// R2#65: keep this in sync with DEFAULT_INVENTORY in
// lambdas/characters/src/index.ts and lambdas/leaderboards/src/index.ts.
// We don't have a shared module for these defaults yet (the lambdas and
// the web app don't share a common package for starter inventory), so
// this is a hand-mirrored copy. Without `palettes: ['palette-dusk']`,
// the IconPicker palette tab is locked at character creation even though
// the server grants the dusk palette by default.
const STARTER_INVENTORY: CosmeticInventory = {
  bodies: [0, 1],
  hats: [0],
  faces: [0, 1, 2],
  hues: [0, 25, 50, 180, 250],
  accentHues: [0, 25, 50, 180, 250],
  titles: ['Tavern Regular'],
  palettes: ['palette-dusk'],
};

export function CharacterCreate({ onCancel, onCreated }: Props) {
  const [name, setName] = useState('');
  const [icon, setIcon] = useState<IconValue>({ set: 'tavern-2', id: 0, hue: 25, body: 0, hat: 0, face: 0, accentHue: 50 });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Stage 2 — after createCharacter succeeds, show a prominent PIN-set
  // step before navigating away. Setting a PIN at creation time keeps
  // the character recoverable across devices; we don't want users to
  // discover this only as a slim ribbon on the home screen.
  const [created, setCreated] = useState<Character | null>(null);
  const [pinBusy, setPinBusy] = useState(false);
  const [pinError, setPinError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      const c = await createCharacter(name, icon);
      setCreated(c);
      setBusy(false);
    } catch (e) {
      const code = e instanceof ApiError ? e.code : 'unknown';
      setErr(REASONS[code] ?? 'Something went wrong.');
      setBusy(false);
    }
  }

  async function submitPin(pin: string) {
    if (!created) return;
    setPinBusy(true);
    setPinError(null);
    try {
      await setCharacterPin(created.charId, pin);
      onCreated({ ...created, hasPin: true });
    } catch (e) {
      if (e instanceof ApiError && e.code === 'pin-already-set') {
        setPinError('A PIN is already set on this character.');
      } else {
        setPinError('Could not set PIN. Try again.');
      }
      setPinBusy(false);
    }
  }

  function skipPin() {
    if (!created || pinBusy) return;
    onCreated(created);
  }

  return (
    <main className="create-screen">
      <form onSubmit={submit} className="create-form" noValidate>
        <div className="create-preview">
          <IconAvatar icon={icon} size={104} alt="preview" />
          <input
            type="text"
            maxLength={20}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="What do they call you?"
            className="create-name"
            autoFocus
            aria-label="character name"
          />
        </div>

        <IconPicker value={icon} onChange={setIcon} inventory={STARTER_INVENTORY} />

        {err && <div role="alert" className="gate-error">{err}</div>}

        <div className="create-actions">
          <button type="button" className="btn ghost" onClick={onCancel} disabled={busy}>Back</button>
          <button type="submit" className="btn primary" disabled={busy || name.trim().length < 2}>
            {busy ? 'Hanging…' : 'Hang on the wall'}
          </button>
        </div>
      </form>
      {created && (
        <PinPad
          mode="set"
          title={`Lock ${created.name} with a PIN`}
          subtitle="A 4-digit PIN keeps this character yours across devices. You can change it later."
          error={pinError}
          busy={pinBusy}
          cancelLabel="Skip for now"
          onCancel={skipPin}
          onSubmit={(pin) => void submitPin(pin)}
        />
      )}
    </main>
  );
}
